const BLOCK_NODE_TYPES = new Set([
  'paragraph',
  'heading',
  'listItem',
  'blockquote',
  'codeBlock'
]);

export const extractDocumentJson = (value) => {
  if (!value || typeof value !== 'object') return null;

  if (value.type === 'doc') return value;

  if (
    Object.prototype.hasOwnProperty.call(value, 'json') &&
    value.json &&
    typeof value.json === 'object' &&
    value.json.type === 'doc'
  ) {
    return value.json;
  }

  if (value.content_json && typeof value.content_json === 'object' && value.content_json.type === 'doc') {
    return value.content_json;
  }

  if (value.content && typeof value.content === 'object' && value.content.type === 'doc') {
    return value.content;
  }

  return null;
};

const extractHtml = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value && typeof value.html === 'string') return value.html;
  if (value && value.content && typeof value.content.html === 'string') return value.content.html;
  return '';
};

const normalizeText = (value = '') => String(value || '')
  .toLowerCase()
  .replace(/\u00a0/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeHtml = (value = '') => String(value || '')
  .replace(/\u00a0/g, ' ')
  .replace(/>\s+</g, '><')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeImageSrc = (value = '') => String(value || '').trim();

const getTextFromNode = (node) => {
  if (!node || typeof node !== 'object') return '';

  if (node.type === 'text') {
    return typeof node.text === 'string' ? node.text : '';
  }

  if (node.type === 'hardBreak') {
    return '\n';
  }

  const children = Array.isArray(node.content) ? node.content : [];
  const inner = children.map(getTextFromNode).join('');

  if (BLOCK_NODE_TYPES.has(node.type)) {
    return `${inner}\n`;
  }

  return inner;
};

const getRenderedStreamFromNode = (node) => {
  if (!node || typeof node !== 'object') return '';

  if (node.type === 'text') {
    return typeof node.text === 'string' ? node.text : '';
  }

  if (node.type === 'hardBreak') {
    return '\n';
  }

  if (node.type === 'image') {
    const src = normalizeImageSrc(node?.attrs?.src || '');
    return `[img:${src}]`;
  }

  const children = Array.isArray(node.content) ? node.content : [];
  const inner = children.map(getRenderedStreamFromNode).join('');

  if (BLOCK_NODE_TYPES.has(node.type)) {
    return `${inner}\n`;
  }

  return inner;
};

const countTiptapNodes = (node, { includeDoc = false } = {}) => {
  if (!node || typeof node !== 'object') return 0;

  const children = Array.isArray(node.content) ? node.content : [];
  const childCount = children.reduce((sum, child) => sum + countTiptapNodes(child, { includeDoc }), 0);

  if (!node.type) return childCount;
  if (!includeDoc && node.type === 'doc') return childCount;
  return 1 + childCount;
};

const countImageNodes = (node) => {
  if (!node || typeof node !== 'object') return 0;
  const children = Array.isArray(node.content) ? node.content : [];
  const childCount = children.reduce((sum, child) => sum + countImageNodes(child), 0);
  return (node.type === 'image' ? 1 : 0) + childCount;
};

const estimateChunkCountFromDoc = (docJson) => {
  const nodes = Array.isArray(docJson?.content) ? docJson.content : [];
  if (!nodes.length) return 0;

  const seen = new Set();
  for (const node of nodes) {
    const chunk = node?.attrs?.chunk;
    if (chunk !== undefined && chunk !== null && String(chunk).trim() !== '') {
      seen.add(String(chunk));
    }
  }

  return seen.size;
};

const getRenderedStreamFromHtml = (html = '') => {
  const normalized = normalizeHtml(html);
  if (!normalized) return '';

  const imgTokenized = normalized.replace(/<img\b[^>]*\bsrc=(['"])(.*?)\1[^>]*>/gi, (_, __, src) => `[img:${normalizeImageSrc(src)}]`);
  const withBreaks = imgTokenized
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h1|h2|h3|h4|h5|h6|blockquote|pre|code)\s*>/gi, '\n');

  return withBreaks
    .replace(/<[^>]+>/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export const extractDocumentText = (value, { normalize = true } = {}) => {
  const docJson = extractDocumentJson(value);
  if (!docJson) return '';
  const text = getTextFromNode(docJson).replace(/\u00a0/g, ' ');
  return normalize ? normalizeText(text) : text;
};

export const computeDocumentStats = (value, overrides = {}) => {
  const docJson = extractDocumentJson(value);
  if (!docJson) {
    return {
      chunks: Number(overrides?.chunks || 0),
      words: Number(overrides?.words || 0),
      chars: Number(overrides?.chars || 0),
      nodes: Number(overrides?.nodes || 0),
      images: Number(overrides?.images || 0)
    };
  }

  const text = extractDocumentText(docJson, { normalize: false });
  const trimmed = text.trim();
  const words = trimmed.length ? trimmed.split(/\s+/).length : 0;
  const images = countImageNodes(docJson);

  return {
    chunks: Number(overrides?.chunks ?? estimateChunkCountFromDoc(docJson)),
    words: Number(overrides?.words ?? words),
    chars: Number(overrides?.chars ?? text.length),
    nodes: Number(overrides?.nodes ?? countTiptapNodes(docJson, { includeDoc: false })),
    images: Number(overrides?.images ?? images)
  };
};

export const normalizeDocumentStats = (stats) => ({
  chunks: Number(stats?.chunks || 0),
  words: Number(stats?.words || 0),
  chars: Number(stats?.chars || 0),
  nodes: Number(stats?.nodes || 0),
  images: Number(stats?.images || 0)
});

export const computeOffsetFromStats = (previousStats, nextStats, { signatureChanged = true } = {}) => {
  const prev = normalizeDocumentStats(previousStats || {});
  const next = normalizeDocumentStats(nextStats || {});

  const charsDelta = Math.abs(next.chars - prev.chars);
  const wordsDelta = Math.abs(next.words - prev.words);
  const nodesDelta = Math.abs(next.nodes - prev.nodes);
  const imagesDelta = Math.abs(next.images - prev.images);

  if (charsDelta > 0) return charsDelta;

  const structuralDelta = (wordsDelta * 2) + (nodesDelta * 2) + (imagesDelta * 8);
  if (structuralDelta > 0) return structuralDelta;

  return signatureChanged ? 1 : 0;
};

const buildOffsetComparableString = (value) => {
  const docJson = extractDocumentJson(value);
  if (docJson) {
    return getRenderedStreamFromNode(docJson);
  }

  const html = extractHtml(value);
  if (html) {
    return getRenderedStreamFromHtml(html);
  }

  const text = extractDocumentText(value, { normalize: false });
  const imageCount = countImageNodes(docJson);
  const imageMarkers = imageCount > 0 ? `[img:]`.repeat(imageCount) : '';
  return `${text}${imageMarkers}`;
};

export const computeContentOffset = (previousValue, nextValue) => {
  const oldComparable = buildOffsetComparableString(previousValue ?? null);
  const newComparable = buildOffsetComparableString(nextValue ?? null);

  let start = 0;
  const oldLen = oldComparable.length;
  const newLen = newComparable.length;

  while (start < oldLen && start < newLen && oldComparable[start] === newComparable[start]) {
    start += 1;
  }

  let endOld = oldLen - 1;
  let endNew = newLen - 1;
  while (endOld >= start && endNew >= start && oldComparable[endOld] === newComparable[endNew]) {
    endOld -= 1;
    endNew -= 1;
  }

  const charsMinus = Math.max(0, endOld - start + 1);
  const charsPlus = Math.max(0, endNew - start + 1);
  const totalOffset = charsPlus + charsMinus;

  return {
    charsPlus,
    charsMinus,
    totalOffset
  };
};

const buildNGramVector = (text = '', n = 3) => {
  const vector = new Map();
  const normalized = String(text || '');
  if (!normalized) return vector;

  if (normalized.length <= n) {
    vector.set(normalized, 1);
    return vector;
  }

  for (let index = 0; index <= normalized.length - n; index += 1) {
    const gram = normalized.slice(index, index + n);
    vector.set(gram, (vector.get(gram) || 0) + 1);
  }

  return vector;
};

const vectorMagnitude = (vector) => {
  let sum = 0;
  vector.forEach((count) => {
    sum += count * count;
  });
  return Math.sqrt(sum);
};

const cosineSimilarity = (leftVector, rightVector) => {
  if (!leftVector.size && !rightVector.size) return 1;
  if (!leftVector.size || !rightVector.size) return 0;

  const [small, large] = leftVector.size <= rightVector.size
    ? [leftVector, rightVector]
    : [rightVector, leftVector];

  let dot = 0;
  small.forEach((value, key) => {
    dot += value * (large.get(key) || 0);
  });

  const denominator = vectorMagnitude(leftVector) * vectorMagnitude(rightVector);
  if (!denominator) return 0;

  return dot / denominator;
};

const extractNormalizedDocumentText = (value, { normalize = true } = {}) => {
  const text = extractDocumentText(value, { normalize: false });
  return normalize ? normalizeText(text) : text;
};

export const computeTextNGramCosineSimilarity = (leftText, rightText, { n = 3, normalize = true } = {}) => {
  const left = normalize ? normalizeText(leftText) : String(leftText || '');
  const right = normalize ? normalizeText(rightText) : String(rightText || '');

  if (!left && !right) return 1;
  if (!left || !right) return 0;

  const leftVector = buildNGramVector(left, n);
  const rightVector = buildNGramVector(right, n);
  return cosineSimilarity(leftVector, rightVector);
};

export const computeDocumentTextSimilarity = (previousValue, nextValue, options = {}) => {
  const previousText = typeof previousValue === 'string'
    ? previousValue
    : extractNormalizedDocumentText(previousValue, { normalize: true });
  const nextText = typeof nextValue === 'string'
    ? nextValue
    : extractNormalizedDocumentText(nextValue, { normalize: true });

  return computeTextNGramCosineSimilarity(previousText, nextText, options);
};
